import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import kivoIcon from '../assets/logo/kivo-icon-only.png';

export const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-6 pt-10 pb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Kivo
        </Link>
        <div className="flex items-center gap-2 mb-8">
          <img src={kivoIcon} alt="Kivo" className="h-8 w-auto" />
          <span className="text-lg font-bold text-slate-800 font-heading">Kivo</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pb-20">
        <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">Terms of Service</h1>
        <p className="text-sm text-slate-400 mb-10">Last updated: March 23, 2026</p>

        <div className="prose prose-slate prose-sm max-w-none space-y-8">
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">1. Service description</h2>
            <p className="text-slate-600 leading-relaxed">
              Kivo is a caregiver coordination service that helps parents stay informed about their child's
              school day. The service includes: automated school email monitoring and task extraction,
              WhatsApp-based caregiver check-ins, and structured daily summaries delivered via WhatsApp
              and a web dashboard.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">2. Eligibility</h2>
            <p className="text-slate-600 leading-relaxed">
              You must be a parent or legal guardian of a school-age child to use Kivo.
              By creating an account, you confirm that you have the authority to provide information
              about your child and to authorize caregiver communications on your behalf.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">3. Subscription and payment</h2>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>Kivo is a paid subscription service at Rs. 1,499 per month per family.</li>
              <li>Payment is processed through Razorpay. By subscribing, you agree to Razorpay's terms of service.</li>
              <li>Subscriptions are billed monthly and renew automatically unless cancelled.</li>
              <li>You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period.</li>
              <li>No refunds are provided for partial months.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">4. Your responsibilities</h2>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>You are responsible for the accuracy of information you provide (child details, school info, caregiver contacts).</li>
              <li>You must obtain your caregiver's consent before adding their WhatsApp number to Kivo.</li>
              <li>You must not use Kivo for any purpose other than coordinating your child's care.</li>
              <li>You are responsible for keeping your account credentials secure.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">5. Gmail access</h2>
            <p className="text-slate-600 leading-relaxed">
              When you connect your Gmail account, Kivo accesses only emails from school senders you
              have explicitly configured. We do not access personal, financial, or unrelated emails.
              You may disconnect Gmail access at any time from your Settings page.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">6. WhatsApp communications</h2>
            <p className="text-slate-600 leading-relaxed">
              Kivo sends automated WhatsApp messages to caregivers and parents as part of the service.
              Message frequency depends on your configuration but typically includes a morning briefing,
              pickup check-ins, and an evening summary on school days. You may adjust message preferences
              in your Settings.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">7. AI-generated content</h2>
            <p className="text-slate-600 leading-relaxed">
              Kivo uses AI to extract tasks from school emails and generate daily summaries. While we
              strive for accuracy, AI-generated content may occasionally be incorrect or incomplete.
              Parents should verify critical information (deadlines, emergency notices) directly with
              the school. Kivo is not a substitute for direct school communication.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">8. Limitation of liability</h2>
            <p className="text-slate-600 leading-relaxed">
              Kivo is provided "as is" without warranty of any kind. We are not liable for missed
              messages, incorrect task extraction, delayed alerts, or any action taken based on
              information provided by the service. Our total liability is limited to the amount
              you have paid for the service in the preceding 3 months.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">9. Account termination</h2>
            <p className="text-slate-600 leading-relaxed">
              You may delete your account at any time by contacting us. We may suspend or terminate
              accounts that violate these terms, engage in abusive behavior, or fail to pay subscription fees.
              Upon termination, your data will be deleted in accordance with our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">10. Changes to terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update these terms from time to time. Material changes will be communicated via
              email or WhatsApp. Continued use of the service after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">11. Governing law</h2>
            <p className="text-slate-600 leading-relaxed">
              These terms are governed by the laws of India. Any disputes will be subject to the
              exclusive jurisdiction of courts in Ahmedabad, Gujarat.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">12. Contact</h2>
            <p className="text-slate-600 leading-relaxed">
              For questions about these terms, contact us at:{' '}
              <a href="mailto:hello@kivo.family" className="text-primary hover:underline">hello@kivo.family</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
